import { Injectable, inject, signal } from '@angular/core';
import { IBizEntity, FieldTypes, BizInformationTypes, BizEntityFunctionTypes, IBizEntityType } from '@optima-stride-ai/core';
import { ExplorerInterceptsFor, ILayout, IListViewItem } from '@optima-stride-ai/optima-components';
import { EntityFileUrlService } from './entity-file-url.service';
import { FooterCaptionDefaults, FooterCaptionKeys } from '../shared/enums/app-constants';
import { AppTagComponent, AppTagService } from '@optima-stride-ai/optima-app-framework';

/**
 * Global service for generating entity layout configurations
 * Following OOPS principles with proper encapsulation and single responsibility
 * 
 * This service provides a centralized way to generate consistent layout configurations
 * for IBizEntity objects across the application (dashboard, libraries, etc.)
 * 
 * Features:
 * - Type-based entity handling (files vs folders)
 * - Signal-based reactive thumbnail URLs for INSTANT display when API responds
 * - Zero-delay rendering: thumbnails appear immediately (no 12-15 second delay)
 * - Consistent thumbnail and footer component integration
 * - Complete layout with footer, footerCaption, and svgBackground
 * - Reusable across multiple components
 * 
 * Performance Optimization:
 * - Uses Angular signals for reactive thumbnail URL updates
 * - Getter function makes content reactive to signal changes
 * - No manual change detection needed - Angular handles it automatically
 * - API call happens in background, updates trigger instant UI refresh
 */

/**
 * Cache entry for layout configurations with signal-based thumbnail URL
 */
interface LayoutCacheEntry {
  config: ILayout<IBizEntity>;
  thumbnailUrl: ReturnType<typeof signal<string>>;
  isLoading: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class EntityLayoutConfigService {
  private readonly entityFileUrlService = inject(EntityFileUrlService);
  private readonly appTagService = inject(AppTagService);
  
  /**
   * Cache for layout configurations with reactive thumbnail URLs
   * Key: entity.id
   * Value: { config: ILayout<IBizEntity>, thumbnailUrl: Signal<string>, isLoading: boolean }
   * 
   * Cache is essential to prevent infinite re-rendering in Angular templates
   */
  private layoutConfigCache = new Map<string | number, LayoutCacheEntry>();

  /**
   * Tag data stored in signals for reactive updates
   * These are populated asynchronously but used synchronously in layout configs
   */
  private availableTags = signal<IBizEntity[]>([]);
  private assignedTagsByEntityId = signal<Record<string, IBizEntity[]>>({});
  private tagBizEntityType = signal<IBizEntityType | null>(null);

  /**
   * Generate or retrieve cached layout configuration for an entity (Dashboard view)
   * 
   * @param entity - The business entity to generate layout for
   * @returns Complete layout configuration object WITHOUT size constraints
   * 
   * CRITICAL: Caching is REQUIRED to prevent infinite loops in Angular templates
   * When templates call getLayoutConfig(), returning new objects each time triggers
   * change detection loops. Cache ensures stable object references.
   * 
   * 
   * 
   */

  /**
   * Fetch and cache tag data for a list of entities
   * This should be called by components (like dashboard) when entities are loaded
   * 
   * @param entities - List of entities to fetch tag data for
   */
  async fetchTagDataForEntities(entities: IBizEntity[]): Promise<void> {
    if (!entities || entities.length === 0) {
      return;
    }

    const entityIds = entities.map(entity => entity.id).filter(Boolean);

    try {
      const [availableTags, tagType, assignedTags] = await Promise.all([
        this.appTagService.getAvailableTags(),
        this.appTagService.getSystemTagBizEntityType(),
        entityIds.length > 0
          ? this.appTagService.getAssignedTags(entityIds)
          : Promise.resolve({} as Record<string, IBizEntity[]>),
      ]);

      // Update signals - this will reactively update all layouts using these values
      this.availableTags.set(availableTags);
      this.tagBizEntityType.set(tagType);
      this.assignedTagsByEntityId.set(assignedTags);

      // Update tag inputs in all cached layout configs
      this.updateTagInputsInCache(availableTags, assignedTags, tagType);
    } catch (error) {
      console.error('Failed to fetch tag data:', error);
      // Set empty values on error
      this.availableTags.set([]);
      this.tagBizEntityType.set(null);
      this.assignedTagsByEntityId.set({});
    }
  }

  /**
   * Update tag component inputs in all cached layout configs
   * Component inputs are not reactive, so we need to manually update them
   */
  private updateTagInputsInCache(
    availableTags: IBizEntity[],
    assignedTags: Record<string, IBizEntity[]>,
    tagType: IBizEntityType | null
  ): void {
    this.layoutConfigCache.forEach((cacheEntry) => {
      const tagsCol = cacheEntry.config.footerCaption?.colsData?.find(c => c.key === 'tags');
      if (tagsCol && tagsCol.value && typeof tagsCol.value === 'object' && 'inputs' in tagsCol.value) {
        // Update the component inputs with the new tag data
        tagsCol.value.inputs = {
          ...tagsCol.value.inputs,
          getAvailableTags: availableTags,
          getAssignedTags: assignedTags,
          tagBizEntityType: tagType,
        };
      }
    });
  }

  getLayoutConfig(entity: IBizEntity): ILayout<IBizEntity> {
    // Guard: skip completely null/undefined entities
    if (!entity) {
      return {} as ILayout<IBizEntity>;
    }
    // Use a stable cache key; fall back to '_empty' for entities whose id is falsy
    const cacheKey = entity.id || '_empty';
    // Check cache first - CRITICAL for preventing infinite loops
    const cachedEntry = this.layoutConfigCache.get(cacheKey);

    if (cachedEntry) {
      // Update entity reference in cached config to reflect latest data
      if (cachedEntry.config.content?.sections?.[0]?.inputs) {
        cachedEntry.config.content.sections[0].inputs['entity'] = entity;
      }
      // Update footerCaption colsData to reflect any renames in real time
      const colsData = cachedEntry.config.footerCaption?.colsData;
      if (colsData) {
        const iconCol = colsData.find(c => c.key === FooterCaptionKeys.FILE_ICON);
        if (iconCol) iconCol.value = entity.icon || FooterCaptionDefaults.ICON;
        const nameCol = colsData.find(c => c.key === FooterCaptionKeys.FILE_NAME);
        if (nameCol) nameCol.value = entity.name || FooterCaptionDefaults.NAME;
      }
      return cachedEntry.config;
    }

    // Create thumbnail URL signal for reactive updates
    const thumbnailUrl = signal<string>('');

    // Create new configuration (without size constraints for dashboard)
    const config = this.createLayoutConfig(entity, false, thumbnailUrl);

    // Cache BEFORE triggering API call to prevent multiple calls
    this.layoutConfigCache.set(cacheKey, {
      config,
      thumbnailUrl,
      isLoading: true
    });

    // Fetch file URL asynchronously and update signal when available (only for entities with a real ID)
    if (entity?.id && entity?.type) {
      this.fetchAndUpdateThumbnailUrl(entity, thumbnailUrl);
    }

    return config;
  }

  /**
   * Generate or retrieve cached layout configuration for an entity (Explorer view)
   * 
   * @param entity - The business entity to generate layout for
   * @returns Complete layout configuration object WITH size constraints
   * 
   * CRITICAL: Caching is REQUIRED to prevent infinite loops in Angular templates
   * Uses separate cache key to maintain different configs for dashboard vs explorer
   */
  getLayoutConfigForExplorer(entity: IBizEntity): ILayout<IBizEntity> {
    // Use separate cache key for explorer view
    const explorerCacheKey = `explorer_${entity.id}`;
    const cachedEntry = this.layoutConfigCache.get(explorerCacheKey);

    if (cachedEntry) {
      // Update entity reference in cached config to reflect latest data
      if (cachedEntry.config.content?.sections?.[0]?.inputs) {
        cachedEntry.config.content.sections[0].inputs['entity'] = entity;
      }
      // Update footerCaption colsData to reflect any renames in real time
      const colsData = cachedEntry.config.footerCaption?.colsData;
      if (colsData) {
        const iconCol = colsData.find(c => c.key === FooterCaptionKeys.FILE_ICON);
        if (iconCol) iconCol.value = entity.icon || FooterCaptionDefaults.ICON;
        const nameCol = colsData.find(c => c.key === FooterCaptionKeys.FILE_NAME);
        if (nameCol) nameCol.value = entity.name || FooterCaptionDefaults.NAME;
      }
      return cachedEntry.config;
    }

    // Create thumbnail URL signal for reactive updates
    const thumbnailUrl = signal<string>('');

    // Create new configuration (with size constraints for library/explorer)
    const config = this.createLayoutConfig(entity, true, thumbnailUrl);

    // Cache BEFORE triggering API call
    this.layoutConfigCache.set(explorerCacheKey, {
      config,
      thumbnailUrl,
      isLoading: true
    });

    // Fetch file URL asynchronously and update signal when available
    // Only for operational entities
    if (entity?.type?.functionType === BizEntityFunctionTypes.Operational) {
      this.fetchAndUpdateThumbnailUrl(entity, thumbnailUrl);
    }

    return config;
  }



  /**
   * Create a new layout configuration for an entity
   * 
   * @param entity - The business entity
   * @param withSizeConstraints - Whether to include maxHeight, minHeight, maxWidth, minWidth
   * @param thumbnailUrl - Signal for reactive thumbnail URL updates
   * @returns New layout configuration object
   * 
   * Configuration structure:
   * - Content section: Contains image URL (reactively updated via signal from API)
   * - SVG Background: Folder/file background for visual consistency
   * - Footer: Pagination and navigation controls
   * - Footer Caption: Entity information display with actions
   * 
   * Note: The file URL signal is updated asynchronously and triggers instant reactive updates
   */
  private createLayoutConfig(
    entity: IBizEntity,
    withSizeConstraints: boolean = false,
    thumbnailUrl: ReturnType<typeof signal<string>>
  ): ILayout<IBizEntity> {
    const config: ILayout<IBizEntity> = {
      content: {
        sections: [
          {
            key: `section-${entity.id}`,
            title: '',
            // ⚡ PERFORMANCE OPTIMIZATION: Use getter for reactive content
            // This makes content reactive to signal changes, enabling INSTANT updates
            // When API responds, signal.set() triggers automatic re-evaluation
            // Result: Thumbnails appear immediately (0ms delay) when data arrives
            get content() {
              return thumbnailUrl();
            },
            contentType: 'image',
            order: 1,
            cssClass: 'border-none',
            inputs: {
              entity: entity
            }
          }
        ],
        layout: 'row',
        padding: 'md',
        cssClass: ''
      },
      // Only add size constraints for library/explorer view
      isResizable: true,
      ...(withSizeConstraints && {
        maxHeight: '20rem',
        maxWidth: '20rem',
        minHeight: '20rem',
        minWidth: '20rem',
      }),
      size: 'lg' as const,
      border: false,
      // SVG background for visual consistency across all views
      svgBackground: entity?.type?.functionType === BizEntityFunctionTypes.Structural ? `
      <svg width="322" height="245" viewBox="0 0 322 245" fill="none" xmlns="http://www.w3.org/2000/svg">
<g filter="url(#filter0_d_3107_93908)">
<path d="M295.587 234H26.413C17.1905 234 11.0272 227.062 10 218.375V24.625C10 15.9937 17.3465 9 26.413 9H102.55C112.522 9 121.95 13.3125 128.18 20.7312L144.587 40.25H295.587C304.654 40.25 312 47.2437 312 55.875V218.375C310.973 226.566 304.654 234 295.587 234Z" fill="#AAAAAA"/>
<path d="M10 130.542H150.606V42H24.0606C16.2921 42 10 47.6604 10 54.6488V130.542Z" fill="white"/>
<path d="M295.587 42.5C304.357 42.5 311.5 49.9284 311.5 59.1426V216.857C311.5 226.072 304.357 233.5 295.587 233.5H26.4131C17.643 233.5 10.5002 226.072 10.5 216.857V72.8574C10.5 63.6431 17.6429 56.2139 26.4131 56.2139H113.441C122.618 56.2139 131.587 53.3774 139.22 48.0605L147.206 42.5H295.587Z" fill="#EEEEEE" stroke="#EEEEEE"/>
<rect x="34.9863" y="63.2579" width="253.76" height="138.384" id="biz-entity-content-display-area"/>
</g>
<defs>
<filter id="filter0_d_3107_93908" x="0" y="0" width="322" height="245" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
<feFlood flood-opacity="0" result="BackgroundImageFix"/>
<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
<feOffset dy="1"/>
<feGaussianBlur stdDeviation="5"/>
<feComposite in2="hardAlpha" operator="out"/>
<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3107_93908"/>
<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_3107_93908" result="shape"/>
</filter>
</defs>
</svg>

` : `
<svg width="194" height="248" viewBox="0 0 194 248" fill="none" xmlns="http://www.w3.org/2000/svg">
<g filter="url(#filter0_d_3110_93757)">
<path d="M184 44.5V225C184 231.63 178.809 237 172.4 237H21.6C15.191 237 10 231.63 10 225V21C10 14.37 15.191 9 21.6 9H147.5L184 44.5Z" fill="white"/>
<path d="M147 9V38.4545C147 42.0709 150.01 45 153.727 45H184L147 9Z" fill="#AAAAAA"/>
</g>
<rect x="34.5" y="51.5" width="125" height="164" id="biz-entity-content-display-area"/>
<defs>
<filter id="filter0_d_3110_93757" x="0" y="0" width="194" height="248" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
<feFlood flood-opacity="0" result="BackgroundImageFix"/>
<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
<feOffset dy="1"/>
<feGaussianBlur stdDeviation="5"/>
<feComposite in2="hardAlpha" operator="out"/>
<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3110_93757"/>
<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_3110_93757" result="shape"/>
</filter>
</defs>
</svg>

`,
      footerCaption: {
        colsData: [
          {
            key: FooterCaptionKeys.FILE_ICON,
            label: 'File Icon',
            value: entity.icon || FooterCaptionDefaults.ICON,
            metaData: { name: 'fileIcon', label: 'File Icon', type: FieldTypes.text },
            bizInformationType: BizInformationTypes.icon
          },
          {
            key: FooterCaptionKeys.FILE_NAME,
            label: 'File Name',
            value: entity.name || FooterCaptionDefaults.NAME,
            metaData: { name: 'fileName', label: 'File Name', type: FieldTypes.text },
            bizInformationType: BizInformationTypes.text,
            tdCSS: 'display:flex; align-items:center; justify-content:center; flex-grow:1;'
          },
          // {
          //   key: FooterCaptionKeys.TAG,
          //   label: 'Tag',
          //   value: { component: AppTagComponent,  inputs: {bizEntity: entity}, },
          //   metaData: { name: 'tag', label: 'Tag', type: FieldTypes.component },
          //   bizInformationType: BizInformationTypes.component,
          //   tdCSS: 'display:flex; align-items:center; justify-content:center; flex-grow:1;'
          // },
          {
            key: 'tags',
            label: 'Tags',
            value: {
              component: AppTagComponent,
              inputs: {
                getAvailableTags: this.availableTags(),
                getAssignedTags: this.assignedTagsByEntityId(),
                tagBizEntityType: this.tagBizEntityType(),
                bizEntity: entity,
                addNew: true,
              }
            },
            metaData: { name: 'tags', label: 'Tags', type: FieldTypes.component },
            bizInformationType: BizInformationTypes.component
          },
        ],
        actions: [{
          label: 'Action',
          icon: 'cilOptions',
          tooltip: 'Action Button',
          action: (receivedItem: IListViewItem<IBizEntity> | undefined, event?: Event) => {
            // Get the correct item - prefer receivedItem if it has an id, otherwise use closure
            const itemToPass = receivedItem?.id ? receivedItem : entity;
            // For interceptable actions, mutate receivedItem to pass data back to interceptor
            if (receivedItem && !receivedItem.id) {
              // Copy properties from correct item to receivedItem
              Object.assign(receivedItem, itemToPass);
            }

            // When used in Explorer component (libraries), the interceptableFor flag handles it
            // When used standalone (dashboard), dispatch a native contextmenu event
            if (event instanceof MouseEvent && event.target instanceof HTMLElement) {
              // Dispatch a new contextmenu event that will bubble up to the wrapper div
              const contextMenuEvent = new MouseEvent('contextmenu', {
                bubbles: true,
                cancelable: true,
                clientX: event.clientX,
                clientY: event.clientY,
                view: window
              });
              event.target.dispatchEvent(contextMenuEvent);
            }
          },
          interceptableFor: ExplorerInterceptsFor.ContextMenu,
          eventSource: undefined
        }]
      }
    };

    return config;
  }



  /**
   * Fetch file URL from API and update the thumbnail signal for instant reactive display
   * This runs asynchronously after the initial config is returned
   * 
   * @param entity - The business entity
   * @param thumbnailUrl - The signal to update with the fetched thumbnail URL
   */
  private async fetchAndUpdateThumbnailUrl(
    entity: IBizEntity,
    thumbnailUrl: ReturnType<typeof signal<string>>
  ): Promise<void> {
    try {
      const startTime = performance.now();

      // Fetch file URL from API using EntityFileUrlService
      let response = null;
      if (entity?.type?.functionType === BizEntityFunctionTypes.Operational) {
        response = await this.entityFileUrlService.getFileUrl(entity.id);
      }

      const fetchTime = performance.now() - startTime;

      if (response?.thumbUrl) {
        // Update the signal - this triggers instant reactive update!
        thumbnailUrl.set(response.thumbUrl);
      } else {
        console.warn('⚠️ No thumbUrl found in API response for entity:', entity.id);
      }
    } catch (error) {
      console.error('❌ Error fetching thumbnail URL for entity:', entity.id, error);
    }
  }

  /**
   * Clear cache for a specific entity
   * Use when entity is deleted, updated, or unpinned to force fresh data
   * 
   * @param entityId - The ID of the entity to clear from cache
   */
  clearEntityCache(entityId: string | number): void {
    this.layoutConfigCache.delete(entityId);
    this.layoutConfigCache.delete(`explorer_${entityId}`);
  }

  /**
   * Clear all cached configurations
   * Use sparingly - typically only needed when major state changes occur
   */
  clearCache(): void {
    this.layoutConfigCache.clear();
  }
}